import { addDays, parseISO } from 'date-fns';

import { TimeOffSummaryItem, WeekDayBreakdown } from '../../types/time-tracking';

/** One day-column slot in the week grid. */
export interface WeekSlot {
  date: Date;
  breakdown: WeekDayBreakdown | null;
}

/**
 * Build a fixed 7-slot array of week-day breakdowns (Mon..Sun), keyed on the
 * actual calendar dates derived from `weekStart`. This guarantees we render
 * exactly 7 day columns even if `dailyBreakdown` is over-sized (e.g. when the
 * backend's UTC-vs-local boundary off-by-one returns 8 entries) or missing
 * days.
 *
 * Keys use **local** calendar-day tuples; breakdowns coming from the API/mock
 * carry UTC-midnight ISO strings, so we read them via `getUTC*` to get the
 * intended calendar day regardless of viewer timezone.
 */
export const buildWeekSlots = (weekStart: Date, dailyBreakdown: WeekDayBreakdown[]): WeekSlot[] => {
  const localKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

  const byKey = new Map<string, WeekDayBreakdown>();
  for (const d of dailyBreakdown) {
    const parsed = parseISO(d.date);
    const key = `${parsed.getUTCFullYear()}-${parsed.getUTCMonth()}-${parsed.getUTCDate()}`;
    byKey.set(key, d);
  }

  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i);
    return { date, breakdown: byKey.get(localKey(date)) ?? null };
  });
};

/** Hours of time-off for a day, summed across approved + pending entries. */
export const sumTimeOffHours = (entries: TimeOffSummaryItem[] | undefined): number =>
  (entries ?? []).reduce((sum, t) => sum + t.totalHours, 0);
